import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import * as authActions from '../../Redux/Auth/AuthActions';
import ModalNavbar from "./modal-navbar-component.js";
import { DropDownFormatter } from '../../Entities/DropDownFormatter.ts';
import SystemDataService from '../../Services/SystemDataService.js';
import { getNavbarData } from '../../Public/navbar-data.js';

function Navbar() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handleRedirect = useHandleRedirect({ onReduxRedirect: (path) => dispatch(authActions.redirectScreen(path)) });

    const [lastScrollY, setLastScrollY] = useState(0);
    const [showNavbar, setShowNavbar] = useState(true);
    const [activeMega, setActiveMega] = useState(null);
    const [bannerIdx, setBannerIdx] = useState(0);
    const closeTimer = useRef(null);
    const hoverRef = useRef(false);

    const { language, dir, links = [], bannerMessages = [], businessInfo = {} } = useSelector((state) => state.systemData || {});

    const txt = getNavbarData(language);

    const phone = SystemDataService.byLabel(links, 'Phone');
    const logoEntry = SystemDataService.byLabel(links, 'Logo Full')
    const bizMainLogo = Array.isArray(businessInfo) ? businessInfo[0]?.mainLogo : businessInfo?.mainLogo;
    const logoImageUrl = logoEntry?.image || bizMainLogo;

    // Merge banner messages from Redux (live) and static data fallback
    const banners = useMemo(() => {
        const live = (bannerMessages || []).filter(b => b.status !== false && b.text);
        return live.length ? live : (txt.bannerMessages || []);
    }, [bannerMessages, txt.bannerMessages]);

    useEffect(() => {
        const handleScroll = () => {
            const y = window.scrollY;
            setShowNavbar(y <= lastScrollY || y <= 500);
            setLastScrollY(y);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Rotate banner messages
    useEffect(() => {
        if (banners.length <= 1) return;
        const id = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 4000);
        return () => clearInterval(id);
    }, [banners.length]);

    const headerNavBar = useMemo(() => [
        { id: 1, name: txt.home, value: '/', type: 'simple' },
        {
            id: 2,
            name: txt.services,
            value: '/services',
            type: 'services',
            children: (txt.servicesMenu || []).map((s) => ({
                id: s.slug,
                label: s.label,
                href: `/services/${s.slug}`,
            })),
        },
        { id: 3, name: txt.about, value: '/about', type: 'simple' },
        { id: 4, name: txt.ourWork, value: '/our-work', type: 'simple' },
        { id: 5, name: txt.blogs, value: '/blogs', type: 'simple' },
        { id: 6, name: txt.contact, value: '/contact', type: 'simple' },
    ], [txt]);

    const mciroNavFormatter = useMemo(() => new DropDownFormatter(headerNavBar), [headerNavBar]);

    const openMega = (key) => { setActiveMega(key); hoverRef.current = true; if (closeTimer.current) clearTimeout(closeTimer.current); };
    const scheduleClose = () => { hoverRef.current = false; if (closeTimer.current) clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => { if (!hoverRef.current) setActiveMega(null); }, 200); };

    const activeBanner = banners[bannerIdx];

    return (
        <div
            dir={dir}
            className={`fixed top-0 left-0 w-full z-50 transition-transform duration-300 ease-in-out ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}
        >
            {/* Banner strip */}
            {banners.length > 0 && (
                <div className="w-full bg-secondary-400 py-1 px-4 text-center overflow-hidden">
                    {activeBanner && (
                        <button
                            key={bannerIdx}
                            onClick={(e) => activeBanner.value && handleRedirect(e, activeBanner.value)}
                            className="text-white text-xs font-bold uppercase tracking-widest hover:underline underline-offset-2 transition-all w-full truncate"
                        >
                            {activeBanner.text}
                        </button>
                    )}
                </div>
            )}

            {/* Main nav bar */}
            <nav className="w-full bg-black">
                <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
                    <div className="flex items-center justify-between h-16 lg:h-20 gap-6">

                        {/* Logo */}
                        <div className="flex-shrink-0">
                            <button onClick={(e) => handleRedirect(e, '/')} className="flex items-center">
                                {logoImageUrl ? (
                                    <img
                                        src={logoImageUrl}
                                        alt="Tropical Marine Construction"
                                        className="h-10 w-auto object-contain"
                                    />
                                ) : (
                                    <span className="text-white font-black text-lg uppercase tracking-wider">Tropical Marine</span>
                                )}
                            </button>
                        </div>

                        {/* Desktop nav links */}
                        <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
                            {headerNavBar.map((elem) => {
                                const hasDropdown = elem.type === 'services' && elem.children?.length > 0;
                                return (
                                    <div
                                        key={elem.id}
                                        className="relative"
                                        onMouseEnter={() => hasDropdown && openMega(elem.value)}
                                        onMouseLeave={scheduleClose}
                                    >
                                        <button
                                            onClick={(e) => handleRedirect(e, elem.value)}
                                            className="px-3 py-2 text-sm font-semibold text-white/90 whitespace-nowrap hover:text-white hover:underline decoration-secondary-400 decoration-2 underline-offset-4 transition-all flex items-center gap-1"
                                        >
                                            {elem.name}
                                            {hasDropdown && (
                                                <svg className={`w-3 h-3 opacity-60 transition-transform duration-200 ${activeMega === elem.value ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            )}
                                        </button>

                                        {/* Services dropdown */}
                                        {hasDropdown && activeMega === elem.value && (
                                            <div
                                                className="absolute left-0 top-full z-[100] min-w-[260px] bg-white shadow-2xl border-t-2 border-secondary-400 py-1 rounded-b-lg"
                                                onMouseEnter={() => { hoverRef.current = true; if (closeTimer.current) clearTimeout(closeTimer.current); }}
                                                onMouseLeave={scheduleClose}
                                            >
                                                {elem.children.map((item) => (
                                                    <button
                                                        key={item.id}
                                                        className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-gray-50 hover:text-secondary-500 transition-colors font-medium border-b border-gray-50 last:border-0"
                                                        onClick={(e) => { handleRedirect(e, item.href); setActiveMega(null); }}
                                                    >
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Phone + Free Estimate */}
                        <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
                            {phone?.url && (
                                <a href={phone.url} className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-secondary-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                    </svg>
                                    <span className="text-base font-bold text-white">{phone.description || '(239) 542-1004'}</span>
                                </a>
                            )}
                            <button
                                onClick={(e) => handleRedirect(e, '/contact')}
                                className="bg-secondary-400 hover:bg-secondary-500 text-white font-bold text-sm px-5 py-2.5 transition-colors uppercase tracking-wide"
                            >
                                Free Estimate
                            </button>
                        </div>

                        {/* Mobile: phone icon + hamburger */}
                        <div className="lg:hidden flex items-center gap-3">
                            {phone?.url && (
                                <a href={phone.url} className="text-white">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                    </svg>
                                </a>
                            )}
                            <ModalNavbar formatter={mciroNavFormatter} />
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );
}

export default Navbar;
